import { Form, Formik } from "formik";
import { DeviceMobile } from "@phosphor-icons/react";
import { FC } from "react";
import * as yup from "yup";
import { motion } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import useUpdateAccount from "../hooks/useUpdateAccount";
import { useNavigate } from "react-router-dom";
import useGetPhoneOTP from "../hooks/useGetPhoneOTP";
import BasePhoneInput from "./BasePhoneInput";
import useResolveGeo from "../hooks/useResolveGeo";
import {
  defaultPhoneCodeFor,
  phoneInputCountry,
  splitPhoneNumber,
} from "../utils/phoneNumber";

const schema = yup.object().shape({
  phone: yup.string(),
});

const ChanngePhoneNumber: FC<{
  setShowPhoneNumberModal: React.Dispatch<React.SetStateAction<boolean>>;
  /** Setup-task route state to carry to the OTP screen (returnTo etc.). */
  setupState?: unknown;
}> = ({ setShowPhoneNumberModal, setupState }) => {
  const { user, refetch } = useAuth();
  const { country } = useResolveGeo();

  const navigate = useNavigate();

  const { getError, getOTP, gettingPhoneOTP, isGetError } = useGetPhoneOTP({
    onSuccess() {
      navigate("/verify/phone-number?redirect=/home/settings/profile", {
        state: setupState,
      });
      refetch();
      setShowPhoneNumberModal(false);
    },
  });

  const {
    updateAccount,
    updateAccountError,
    isUpdatingAccountError,
    updatingAccount,
  } = useUpdateAccount({
    onSuccess() {
      getOTP();
    },
  });

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed top-0 left-0 bg-deepBlue/80 backdrop-blur-sm w-full h-screen grid place-items-center p-5 z-50"
    >
      <Formik
        validationSchema={schema}
        initialValues={{ phone: "" }}
        onSubmit={(values, { resetForm }) => {
          // Previously this stripped the leading "+" and sent the whole E.164
          // string as `phone` with no `phone_code`, so a number from any other
          // country was stored with the dialling code baked into the national
          // number and the account's old code left in place.
          const { phone_code, phone } = splitPhoneNumber(
            values.phone,
            defaultPhoneCodeFor(user?.phone_code, country)
          );
          updateAccount(
            { phone, phone_code },
            {
              onSuccess() {
                resetForm();
              },
            }
          );
        }}
      >
        {({ setFieldValue, handleBlur }) => {
          return (
            <Form className="bg-white max-w-sm w-full p-5 rounded-xl shadow-sm flex items-center flex-col gap-5">
              <div className="p-2 bg-gray-100 rounded-full ring-8 ring-gray-50">
                <DeviceMobile size={28} />
              </div>
              <div className="flex items-center flex-col">
                <div className="font-medium">Change Your Phone Number</div>
                <p className="text-gray-500 text-center">
                  Enter your new new phone number.
                </p>
              </div>
              <div className="rounded-lg border border-gray-300 shadow flex items-center w-full relative overflow-hidden focus-within:outline-deepBlue focus-within:outline accent-deepBlue">
                <BasePhoneInput
                  international
                  countryCallingCodeEditable={false}
                  defaultCountry={phoneInputCountry(user?.residence_country ?? country)}
                  type="text"
                  id="phone"
                  name="phone"
                  onChange={(phone: string | undefined) => {
                    setFieldValue("phone", phone);
                  }}
                  onBlur={handleBlur("phone")}
                  placeholder="Enter new phone number"
                  className="px-2 bg-transparent flex-1 rounded-lg focus:outline-none"
                />
              </div>
              {isGetError && (
                <div className="text-red-500 text-left flex w-full">
                  {getError?.message}
                </div>
              )}
              {isUpdatingAccountError && (
                <div className="text-red-500 text-left flex w-full">
                  {updateAccountError?.message}
                </div>
              )}
              <div className="flex items-center gap-5 w-full">
                <button
                  type="button"
                  aria-label="Cancel"
                  onClick={() => setShowPhoneNumberModal(false)}
                  className="p-3 px-5 flex-1 border-red-100 border-2 rounded-lg font-bold bg-red-50/50 text-red-500 text-sm"
                >
                  Cancel
                </button>
                <button
                  disabled={updatingAccount || gettingPhoneOTP}
                  type="submit"
                  className="p-3 px-5 flex-1 border-visaro-gray-700 border-2 rounded-lg font-bold bg-deepBlue text-white text-sm"
                >
                  {updatingAccount || gettingPhoneOTP ? "Loading" : "Proceed"}
                </button>
              </div>
            </Form>
          );
        }}
      </Formik>
    </motion.div>
  );
};

export default ChanngePhoneNumber;
